import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ApplicationActionButtons from "@components/form/ApplicationActionButtons";
import Modal from "@components/layout/Modal";
import ApprovalTag from "@components/shared/ApprovalTag";
import EvidenceList from "@components/shared/EvidenceList";
import {
  AdministrativeApprovalNumericType,
  ApprovalExtractionRightVersion,
  ExtractionRightApprovalType,
} from "@services/administrativeApprovals";
import { useAuditAmalgamationOrSubdivision } from "@hooks/mutation/useAuditAmalgamationOrSubdivision";
import { formatDate } from "@utils/formatDate";
import { formatVolume } from "@utils/formatVolume";
import { formatAccountingPeriod } from "@utils/formatAccountingPeriod";
import { toastError, toastSuccess } from "@utils/toast";
import { AdministrativeApprovalNumericStatus } from "@services/administrativeApprovals";
import { formatDateInput } from "@utils/formatDate";
import ConfirmModal from "@components/shared/ConfirmModal";
import Label from "@components/form/Label";
import TextArea from "@components/form/TextArea";
import TimestampInput from "@components/form/TimestampInput";
import {
  getActionByUser,
  getActionTimestamp,
} from "@utils/administrativeApproval";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

type Props = {
  data?: any;
  onClose: () => void;
  refetch?: () => void;
};
const AmalgamateOrSubdivideModal: React.FunctionComponent<Props> = ({
  data,
  onClose,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const [confirmationModal, setConfirmationModal] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [action, setAction] = useState("");
  const [effectiveDate, setEffectiveDate] = useState<Date>();
  const { mutateAsync: auditAmalgamationOrSubdivisionMutation, isLoading } =
    useAuditAmalgamationOrSubdivision();
  const context =
    +data?.type === AdministrativeApprovalNumericType.AME
      ? ExtractionRightApprovalType.Amalgamate
      : ExtractionRightApprovalType.Subdivide;

  const inputExtractionRights = data?.extractionRights.filter(
    (e: any) => +e.version === ApprovalExtractionRightVersion.Old,
  );
  const outputExtractionRights = data?.extractionRights.filter(
    (e: any) => +e.version === ApprovalExtractionRightVersion.New,
  );

  const getExtractionRightNameAndVolume = (extractionRights: any) => {
    return extractionRights
      ?.map((right: any) => `${right.name} (${formatVolume(right.volume)})`)
      .join(", ");
  };

  const handleAudit = async () => {
    if (action !== "approve" && !reason) {
      toastError(
        t("approval.subdivide_and_amalgamate.create.toast.error_reason"),
      );
      return;
    }
    if (action === "approve" && !effectiveDate) {
      toastError(
        t("approval.subdivide_and_amalgamate.create.toast.error_date"),
      );
      return;
    }
    try {
      await auditAmalgamationOrSubdivisionMutation({
        administrativeApprovalId: data?.id,
        effectiveDate: effectiveDate,
        action,
        reason,
      });
      toastSuccess(
        t("approval.subdivide_and_amalgamate.create.toast.success", {
          context,
        }),
      );
      onClose();
    } catch (error) {
      toastError(
        <p>
          {t("approval.subdivide_and_amalgamate.create.toast.error", {
            context,
          })}
        </p>,
      );
    } finally {
      setShowConfirmModal(false);
      setEffectiveDate(undefined);
      setReason("");
      setAction("");
      refetch?.();
      onClose();
    }
  };

  return (
    <>
      <Modal open={data && confirmationModal === ""}>
        <div className="flex flex-col">
          <div className="border-b p-4">
            <h3 className="text-lg font-bold leading-6 text-gray-900 capitalize">
              {t("approval.subdivide_and_amalgamate.long_title", { context })}
            </h3>
          </div>
          <div className="p-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-1 mb-4">
              <div className="col-span-2 sm:col-span-1 flex gap-1">
                <dt className="font-medium text-gray-900">
                  {t("common.level1wrs")}:
                </dt>
                <dd className="text-gray-500">{data?.level1Resource.name}</dd>
              </div>
              <div className="col-span-2 sm:col-span-1 flex gap-1">
                <dt className="font-medium text-gray-900">
                  {t("common.accounting_period")}:
                </dt>
                <dd className="text-gray-500">
                  {formatAccountingPeriod(data?.accountingPeriod)}
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-1 flex gap-1">
                <dt className="font-medium text-gray-900">
                  {t("common.subscriber")}:
                </dt>
                <dd className="text-gray-500">
                  <a
                    className="flex flex-row items-baseline gap-1 hover:underline hover:text-gray-900"
                    href={`/polestar/subscribers/${data?.buyerId}/level0_resources/${data?.level0ResourceId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {data?.buyer?.name}
                    <ArrowTopRightOnSquareIcon className="w-4 h-4 opacity-70 group-hover:opacity-100 transition-opacity" />
                  </a>
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-1 flex gap-1">
                <dt className="font-medium text-gray-900">
                  {t(
                    "approval.subdivide_and_amalgamate.approval_modal.input_extraction_rights",
                  )}
                  :
                </dt>
                <dd className="text-gray-500">
                  {getExtractionRightNameAndVolume(inputExtractionRights)}
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-1 flex gap-1">
                <dt className="font-medium text-gray-900">
                  {t(
                    "approval.subdivide_and_amalgamate.approval_modal.output_extraction_rights",
                  )}
                  :
                </dt>
                <dd className="text-gray-500">
                  {getExtractionRightNameAndVolume(outputExtractionRights)}
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-1 flex gap-1">
                <dt className="font-medium text-gray-900">
                  {t("extraction_right.total_volume")}:
                </dt>
                <dd className="text-gray-500">{formatVolume(data?.volume)}</dd>
              </div>
              <div className="col-span-2 sm:col-span-1 flex gap-1">
                <dt className="font-medium text-gray-900">
                  {t("common.status")}:
                </dt>
                <dd className="text-gray-500">
                  <ApprovalTag
                    isAccounted={Boolean(data?.accountedAt)}
                    status={data?.status}
                  />
                </dd>
              </div>
              {getActionByUser(data) && (
                <>
                  <div className="col-span-2 sm:col-span-1 flex gap-1">
                    <dt className="font-medium text-gray-900">
                      {t("common.action_by")}:
                    </dt>
                    <dd className="text-gray-500">
                      {getActionByUser(data)?.name}
                    </dd>
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex gap-1">
                    <dt className="font-medium text-gray-900">
                      {t("common.action_date")}:
                    </dt>
                    <dd className="text-gray-500">
                      {getActionTimestamp(data)}
                    </dd>
                  </div>
                </>
              )}
              {data?.status ===
                AdministrativeApprovalNumericStatus.Approved && (
                <div className="col-span-2 sm:col-span-1 flex gap-1 items-center">
                  <dt className="font-medium text-gray-900">
                    {t("common.effective_date")}:
                  </dt>
                  <dd className="text-gray-500">
                    {formatDate(new Date(data?.effectiveDate))}
                  </dd>
                </div>
              )}
              <div className="col-span-2 sm:col-span-1 flex gap-1 items-center">
                <dt className="font-medium text-gray-900">
                  {t("common.note")}:
                </dt>
                <dd className="text-gray-500">
                  {formatDate(
                    new Date(
                      data?.status === 2
                        ? data?.approvedAt
                        : data?.status === 3
                        ? data?.rejectedAt
                        : data?.status === 4
                        ? data?.updatedAt
                        : data?.lodgedAt,
                    ),
                  )}
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-1 flex gap-1">
                <dt className="font-medium text-gray-900">
                  {t("common.effective_date")}:
                </dt>
                <dd className="text-gray-500">
                  {data?.effectiveDate
                    ? formatDate(new Date(data?.effectiveDate))
                    : "-"}
                </dd>
              </div>
            </dl>
          </div>

          {data?.evidences?.length ? (
            <EvidenceList data={data.evidences} />
          ) : null}

          <ApplicationActionButtons
            status={data?.status === 3 ? 2 : data?.status}
            onApprove={() => {
              setAction("approve");
              setShowConfirmModal(true);
            }}
            onReject={() => {
              setAction("reject");
              setShowConfirmModal(true);
            }}
            onRequireMoreInfo={() => {
              setAction("requestInfo");
              setShowConfirmModal(true);
            }}
            onCancel={() => {
              setAction("");
              setConfirmationModal("");
              setReason("");
              setEffectiveDate(undefined);
              onClose();
            }}
            onContinue={() => {
              navigate(
                `/polestar/administrative_approvals/amalgamate_or_subdivide?administrativeApprovalId=${data?.id}`,
              );
            }}
          />
          <ConfirmModal
            open={showConfirmModal}
            onClose={() => {
              setShowConfirmModal(false);
            }}
            onConfirm={handleAudit}
            isSubmitting={isLoading}
            confirmText={t("common.ledger")}
          >
            {t("approval.subdivide_and_amalgamate.create.confirm", { context })}
            {action === "approve" && (
              <>
                <div className=" text-gray-700 mt-2 mb-2">
                  <TimestampInput
                    id="effectiveDate"
                    level1ResourceId={data?.level1ResourceId}
                    value={effectiveDate ? formatDateInput(effectiveDate) : ""}
                    onChange={e => setEffectiveDate(new Date(e.target.value))}
                    required
                  />
                </div>

                <div className=" text-gray-700 mt-2">
                  <Label htmlFor="approveNote" optional>
                    {t("common.approval_note")}
                  </Label>
                  <TextArea
                    id="approveNote"
                    className="text-sm"
                    placeholder={t("common.write_approve_info") as string}
                    onChange={e => setReason(e.target.value)}
                  />
                </div>
              </>
            )}
            {action === "reject" && (
              <div className=" text-gray-700 mt-2">
                <Label htmlFor="rejectNote">
                  {t("common.reason_for_rejection")}
                </Label>
                <TextArea
                  id="rejectNote"
                  className="text-sm"
                  placeholder={t("common.reason_for_rejection") as string}
                  onChange={e => setReason(e.target.value)}
                />
              </div>
            )}
            {action === "requestInfo" && (
              <div className=" text-gray-700 mt-2">
                <Label htmlFor="requestInfoNote">
                  {t("common.request_more_info")}
                </Label>
                <TextArea
                  id="requestInfoNote"
                  className="text-sm"
                  placeholder={t("common.request_info_query") as string}
                  onChange={e => setReason(e.target.value)}
                />
              </div>
            )}
          </ConfirmModal>
        </div>
      </Modal>
    </>
  );
};

export default AmalgamateOrSubdivideModal;
